import { CartAddEvent } from '@theme/events';

class ShoppableVideoCarousel extends HTMLElement {
  connectedCallback() {
    if (this.controller) return;
    this.controller = new AbortController();
    const { signal } = this.controller;
    this.track = this.querySelector('[data-track]');
    this.dialog = this.querySelector('dialog');
    this.reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.userPaused = this.reducedMotion;
    this.busy = false;
    this.visible = false;
    this.motionId = 0;
    this.pausedIndex = null;
    this.watchIndex = null;
    this.defaultLoops = new WeakMap();
    this.setupLoop();
    this.querySelectorAll('[data-card]').forEach((card) => {
      const video = card.querySelector('video');
      if (!video) return;
      this.defaultLoops.set(video, video.loop);
      this.syncSound(card);
      video.addEventListener('volumechange', () => this.syncSound(card), { signal });
      video.addEventListener('playing', () => {
        if (!this.canPlay(card)) { this.pauseVideo(card); return; }
        this.setError(card.querySelector('[data-error]'), '');
        this.syncPlayback(card);
      }, { signal });
      video.addEventListener('pause', () => this.syncPlayback(card), { signal });
      video.addEventListener('ended', () => {
        this.syncPlayback(card);
        this.finishWatch(card);
      }, { signal });
      video.addEventListener('error', () => {
        this.pauseVideo(card);
        this.setError(card.querySelector('[data-error]'), 'This video could not load. Please try again.');
        this.finishWatch(card);
      }, { signal });
    });
    this.addEventListener('click', (event) => this.handleClick(event), { signal });
    this.addEventListener('pointerenter', (event) => { if (event.pointerType === 'mouse') { this.hovered = true; this.stopTimer(); } }, { signal });
    this.addEventListener('pointerleave', () => { this.hovered = false; this.startTimer(); }, { signal });
    this.addEventListener('focusin', () => this.stopTimer(), { signal });
    this.addEventListener('focusout', () => queueMicrotask(() => this.startTimer()), { signal });
    this.track.addEventListener('keydown', (event) => {
      if (event.target !== this.track || !['ArrowLeft', 'ArrowRight'].includes(event.key)) return;
      event.preventDefault();
      this.advance(event.key === 'ArrowRight' ? 1 : -1);
    }, { signal });
    this.track.addEventListener('scroll', () => {
      this.pauseNonCenteredVideos();
      if (this.moving) return;
      window.clearTimeout(this.settleTimer);
      this.settleTimer = window.setTimeout(() => this.settleScroll(), 140);
    }, { signal, passive: true });
    this.track.addEventListener('pointerdown', () => this.cancelMotion(), { signal, passive: true });
    this.track.addEventListener('wheel', () => this.cancelMotion(), { signal, passive: true });
    this.resizeObserver = new ResizeObserver(() => {
      this.cancelMotion();
      this.measureLoop();
      this.centerInstant(this.mainCards[Number(this.currentIndex || 0)]);
      this.settleScroll();
    });
    this.resizeObserver.observe(this.track);
    this.dialog.addEventListener('close', () => {
      this.opener?.focus();
      this.settleScroll();
    }, { signal });
    this.dialog.addEventListener('click', (event) => {
      if (event.target !== this.dialog) return;
      const box = this.dialog.getBoundingClientRect();
      if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) this.dialog.close();
    }, { signal });
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) { this.stopTimer(); this.pauseVideos(); } else this.settleScroll();
    }, { signal });
    document.addEventListener('dialog:open', () => { this.cancelMotion(); this.stopTimer(); this.pauseVideos(); }, { signal });
    document.addEventListener('dialog:close', () => this.settleScroll(), { signal });
    document.addEventListener('shopify:block:select', (event) => {
      const card = event.target.closest?.('[data-card]');
      if (card && this.contains(card)) { this.userPaused = true; this.stopTimer(); this.centerCard(card); this.updatePauseLabel(); }
    }, { signal });
    this.observer = new IntersectionObserver(([entry]) => {
      this.visible = entry.isIntersecting;
      if (this.visible) this.settleScroll(); else { this.cancelMotion(); this.stopTimer(); this.pauseVideos(); }
    }, { threshold: 0.15 });
    this.observer.observe(this);
    this.updatePauseLabel();
  }

  disconnectedCallback() {
    this.cancelMotion();
    window.clearTimeout(this.settleTimer);
    this.stopTimer();
    this.pauseVideos();
    this.resizeObserver?.disconnect();
    this.clearWatch();
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = null;
  }

  async handleClick(event) {
    const button = event.target.closest('button');
    if (!button || !this.contains(button) || button.disabled) return;
    if (button.hasAttribute('data-close')) { this.dialog.close(); return; }
    if (button.hasAttribute('data-direction')) { this.advance(Number(button.dataset.direction)); return; }
    if (button.hasAttribute('data-toggle-slide')) {
      this.userPaused = !this.userPaused;
      this.updatePauseLabel();
      this.startTimer();
      return;
    }
    const card = button.closest('[data-card]');
    if (button.hasAttribute('data-play')) {
      const video = card.querySelector('video');
      if (!video) return;
      if (this.isCentered(card) && !video.paused) {
        this.pausedIndex = card.dataset.reelIndex;
        this.pauseVideo(card);
      } else {
        this.pausedIndex = null;
        await this.centerCard(card);
      }
      return;
    }
    if (button.hasAttribute('data-mute')) {
      const video = card.querySelector('video');
      if (!video) return;
      const enableSound = video.muted || video.volume === 0;
      if (enableSound && video.volume === 0) video.volume = 1;
      video.muted = !enableSound;
      this.syncSound(card);
      this.pausedIndex = null;
      this.beginWatch(card);
      await this.centerCard(card);
      return;
    }
    if (button.hasAttribute('data-add')) {
      if (this.busy) return;
      const template = card.querySelector('[data-variants]');
      if (!template) return;
      const variants = template.content.querySelectorAll('[data-variant-id]');
      if (variants.length > 1) {
        this.activeCard = card;
        this.opener = button;
        this.querySelector('[data-dialog-title]').textContent = card.querySelector('[data-product-title]').textContent;
        this.querySelector('[data-dialog-variants]').replaceChildren(template.content.cloneNode(true));
        this.setError(this.querySelector('[data-dialog-error]'), '');
        this.cancelMotion();
        this.pauseVideos();
        this.stopTimer();
        this.dialog.showModal();
      } else if (variants[0] && !variants[0].disabled) {
        await this.addVariant(variants[0], button, card);
      }
      return;
    }
    if (button.hasAttribute('data-variant-id')) await this.addVariant(button, button, this.activeCard);
  }

  async addVariant(variant, button, card) {
    if (this.busy) return;
    this.busy = true;
    const errorNode = this.dialog.open ? this.querySelector('[data-dialog-error]') : card.querySelector('[data-error]');
    this.setError(errorNode, '');
    const original = button.innerHTML;
    button.disabled = true;
    button.textContent = 'Adding…';
    button.setAttribute('aria-busy', 'true');
    const sectionIds = [...new Set([...document.querySelectorAll('cart-items-component')].map((item) => item.dataset.sectionId).filter(Boolean))].slice(0, 5);
    const quantity = Number(variant.dataset.quantity || 1);
    let added = false;
    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: [{ id: variant.dataset.variantId, quantity }], sections: sectionIds, sections_url: window.location.pathname }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this variant. Please try again.');
      added = true;
      // Section rendering can fail independently of a successful cart addition.
      const sections = result.sections || {};
      const missing = sectionIds.filter((id) => !sections[id]);
      if (missing.length) {
        try {
          const url = new URL(window.location.href);
          url.searchParams.set('sections', missing.join(','));
          const rendered = await fetch(url, { headers: { Accept: 'application/json' } });
          if (rendered.ok) Object.assign(sections, await rendered.json());
        } catch { /* Existing cart components also retry missing sections. */ }
      }
      let cartResource;
      for (const html of Object.values(sections)) {
        if (typeof html !== 'string') continue;
        const count = new DOMParser().parseFromString(html, 'text/html').querySelector('[ref="cartItemCount"]');
        if (count) { cartResource = { item_count: Number(count.textContent.trim()) }; break; }
      }
      if (this.dialog.open) this.dialog.close();
      this.pauseVideos();
      this.dispatchEvent(new CartAddEvent(cartResource, this.dataset.sectionId, {
        source: 'product-form-component', sections, itemCount: quantity, variantId: variant.dataset.variantId,
      }));
      const drawer = document.querySelector('cart-drawer-component');
      if (drawer) {
        await customElements.whenDefined('cart-drawer-component');
        drawer.open();
      } else {
        // The cart template intentionally has no drawer.
        window.location.assign(Theme.routes.cart_url || `${window.Shopify?.routes?.root || '/'}cart`);
      }
      this.querySelector('[data-status]').textContent = 'Product added to your cart.';
    } catch (error) {
      this.setError(errorNode, added ? 'Product added. Open your cart to review it.' : error.message || 'Unable to add this product. Please try again.');
    } finally {
      button.innerHTML = original;
      button.disabled = false;
      button.removeAttribute('aria-busy');
      this.busy = false;
    }
  }

  setError(node, message) { node.textContent = message; node.hidden = !message; }
  syncSound(card) {
    const video = card.querySelector('video');
    const button = card.querySelector('[data-mute]');
    if (!video || !button) return;
    const soundOn = !video.muted && video.volume > 0;
    button.setAttribute('aria-label', soundOn ? 'Mute video' : 'Unmute video');
    button.setAttribute('aria-pressed', String(soundOn));
    button.querySelector('[data-sound-on]')?.toggleAttribute('hidden', !soundOn);
    button.querySelector('[data-sound-off]')?.toggleAttribute('hidden', soundOn);
  }
  syncPlayback(card) {
    const video = card.querySelector('video');
    const playing = !!video && !video.paused && !video.ended;
    card.classList.toggle('is-playing', playing);
    const button = card.querySelector('[data-play]');
    button.setAttribute('aria-label', playing ? 'Pause video' : 'Play video');
    button.setAttribute('aria-pressed', String(playing));
    button.querySelector('[data-play-symbol]')?.toggleAttribute('hidden', playing);
    button.querySelector('[data-pause-symbol]')?.toggleAttribute('hidden', !playing);
  }
  pauseVideo(card) {
    card.querySelector('video')?.pause();
    this.syncPlayback(card);
  }
  pauseVideos() { this.querySelectorAll('[data-card]').forEach((card) => this.pauseVideo(card)); }
  beginWatch(card) {
    this.clearWatch();
    this.watchIndex = card.dataset.reelIndex;
    this.stopTimer();
    // A watched reel must end once, even though ambient reels normally loop.
    this.track.querySelectorAll('[data-card]').forEach((item) => {
      if (item.dataset.reelIndex === this.watchIndex) {
        const video = item.querySelector('video');
        if (video) video.loop = false;
      }
    });
  }
  clearWatch() {
    if (this.watchIndex === null) return;
    this.track.querySelectorAll('[data-card]').forEach((item) => {
      if (item.dataset.reelIndex === this.watchIndex) {
        const video = item.querySelector('video');
        if (video) video.loop = this.defaultLoops.get(video) ?? true;
      }
    });
    this.watchIndex = null;
  }
  finishWatch(card) {
    if (this.watchIndex !== card.dataset.reelIndex) return;
    this.clearWatch();
    // Keep the completed reel stopped while waiting for the next slide.
    this.pausedIndex = card.dataset.reelIndex;
    this.hovered = false;
    this.startTimer();
  }
  setupLoop() {
    // Reuse originals in the middle; lightweight copies provide continuous edges.
    this.track.querySelectorAll('[data-reel-clone]').forEach((card) => card.remove());
    this.mainCards = [...this.track.querySelectorAll('[data-card]')];
    this.mainCards.forEach((card, index) => { card.dataset.reelIndex = String(index); });
    if (this.mainCards.length > 1) {
      const copy = (card) => {
        const clone = card.cloneNode(true);
        clone.dataset.reelClone = 'true';
        clone.removeAttribute('data-shopify-editor-block');
        clone.querySelectorAll('[id]').forEach((node) => node.removeAttribute('id'));
        clone.querySelectorAll('video').forEach((video) => { video.muted = true; });
        return clone;
      };
      this.track.prepend(...this.mainCards.map(copy));
      this.track.append(...this.mainCards.map(copy));
    }
    this.measureLoop();
    this.currentIndex = Math.floor(this.mainCards.length / 2);
    this.centerInstant(this.mainCards[this.currentIndex]);
  }
  measureLoop() {
    if (!this.mainCards.length) return;
    const card = this.mainCards[0];
    const gap = Number.parseFloat(getComputedStyle(this.track).columnGap) || 0;
    this.step = card.getBoundingClientRect().width + gap;
    this.loopWidth = this.step * this.mainCards.length;
  }
  centerOffset(card) {
    const track = this.track.getBoundingClientRect();
    const rect = card.getBoundingClientRect();
    return rect.left + rect.width / 2 - (track.left + track.width / 2);
  }
  isCentered(card) { return !!card && Math.abs(this.centerOffset(card)) <= 3; }
  nearestCard() {
    return [...this.track.querySelectorAll('[data-card]')].reduce((best, card) =>
      !best || Math.abs(this.centerOffset(card)) < Math.abs(this.centerOffset(best)) ? card : best, null);
  }
  centerInstant(card) {
    if (!card) return;
    this.track.scrollLeft += this.centerOffset(card);
  }
  canPlay(card) {
    return this.visible && !document.hidden && !this.moving && this.isCentered(card) &&
      !this.dialog.open && !document.querySelector('cart-drawer-component dialog[open]');
  }
  pauseNonCenteredVideos() {
    this.querySelectorAll('[data-card]').forEach((card) => {
      const video = card.querySelector('video');
      if (video && !video.paused && !this.canPlay(card)) this.pauseVideo(card);
    });
  }
  async playCentered(card) {
    this.pauseNonCenteredVideos();
    const video = card?.querySelector('video');
    if (!video || !this.canPlay(card) || this.pausedIndex === card.dataset.reelIndex) return;
    if (!video.paused) return;
    try {
      await video.play();
      if (!this.canPlay(card)) { this.pauseVideo(card); return; }
      this.syncPlayback(card);
    } catch (error) {
      if (error.name === 'AbortError' || !this.canPlay(card)) return;
      // Browsers may require a new gesture for previously enabled audio.
      if (!video.muted && error.name === 'NotAllowedError') {
        video.muted = true;
        this.syncSound(card);
        try { await video.play(); this.syncPlayback(card); } catch { this.finishWatch(card); }
      } else {
        this.finishWatch(card);
      }
    }
  }
  settleScroll() {
    if (!this.controller || this.moving) return;
    let card = this.nearestCard();
    if (!card || !this.isCentered(card)) { this.pauseNonCenteredVideos(); return; }
    this.currentIndex = Number(card.dataset.reelIndex);
    if (this.watchIndex !== null && this.watchIndex !== card.dataset.reelIndex) this.clearWatch();
    if (card.hasAttribute('data-reel-clone')) {
      const original = this.mainCards[this.currentIndex];
      const video = card.querySelector('video');
      const originalVideo = original.querySelector('video');
      if (video && originalVideo) {
        originalVideo.muted = video.muted;
        originalVideo.volume = video.volume;
        this.syncSound(original);
      }
      this.pauseVideos();
      const focused = card.contains(document.activeElement) ? document.activeElement : null;
      this.centerInstant(original);
      if (focused) {
        const controls = [...card.querySelectorAll('button,a')];
        original.querySelectorAll('button,a')[controls.indexOf(focused)]?.focus({ preventScroll: true });
      }
      card = original;
    }
    this.track.querySelectorAll('[data-card]').forEach((item) => item.classList.toggle('is-centered', item === card));
    this.playCentered(card);
    this.startTimer();
  }
  cancelMotion() {
    this.motionId += 1;
    cancelAnimationFrame(this.motionFrame);
    this.finishMotion?.(false);
    this.finishMotion = null;
    this.moving = false;
    this.track?.removeAttribute('data-moving');
  }
  async centerCard(card) {
    if (!card) return;
    this.cancelMotion();
    this.stopTimer();
    this.pauseVideos();
    const distance = this.centerOffset(card);
    if (this.reducedMotion || Math.abs(distance) <= 3) {
      this.centerInstant(card);
      this.settleScroll();
      return;
    }
    this.moving = true;
    this.track.setAttribute('data-moving', '');
    const token = this.motionId;
    const start = this.track.scrollLeft;
    const started = performance.now();
    const duration = Math.min(750, 420 + Math.abs(distance) / 5);
    const completed = await new Promise((resolve) => {
      this.finishMotion = resolve;
      const frame = (time) => {
        if (token !== this.motionId) { resolve(false); return; }
        const progress = Math.min(1, (time - started) / duration);
        const eased = progress < 0.5 ? 4 * progress ** 3 : 1 - (-2 * progress + 2) ** 3 / 2;
        this.track.scrollLeft = start + distance * eased;
        if (progress < 1) this.motionFrame = requestAnimationFrame(frame);
        else resolve(true);
      };
      this.motionFrame = requestAnimationFrame(frame);
    });
    if (!completed || token !== this.motionId) return;
    this.finishMotion = null;
    this.moving = false;
    this.track.removeAttribute('data-moving');
    this.settleScroll();
  }
  advance(direction) {
    if (this.moving) return;
    const cards = [...this.track.querySelectorAll('[data-card]')];
    const card = this.nearestCard();
    if (!card || cards.length < 2) return;
    this.pausedIndex = null;
    const target = cards[cards.indexOf(card) + Math.sign(direction)];
    if (target) this.centerCard(target);
  }
  updatePauseLabel() {
    const button = this.querySelector('[data-toggle-slide]');
    if (button) { button.textContent = this.userPaused ? 'Resume auto-slide' : 'Pause auto-slide'; button.setAttribute('aria-pressed', String(this.userPaused)); }
  }
  startTimer() {
    this.stopTimer();
    if (this.watchIndex !== null || this.dataset.autoslide !== 'true' || this.userPaused || this.hovered || !this.visible || document.hidden || this.dialog.open || (this.contains(document.activeElement) && document.activeElement.matches(':focus-visible')) || this.moving || this.mainCards.length < 2 || document.querySelector('cart-drawer-component dialog[open]')) return;
    this.timer = window.setInterval(() => this.advance(1), Number(this.dataset.interval || 6000));
  }
  stopTimer() { window.clearInterval(this.timer); }
}

if (!customElements.get('shoppable-video-carousel')) customElements.define('shoppable-video-carousel', ShoppableVideoCarousel);
